import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { ShellDomainProvider, UtilityService } from '@ecap3/core';
import { deepSafeAccess } from '../helpers/deep-safe-access';

const header: any = new HttpHeaders({
    'Content-Type': 'application/json',
});

export class Directory {
    public ItemId: string = '';
    public Name: string = '';
    public ParentDirectoryId: string = '';
    public Tags: string[] = ['temporary', 'disposable'];

    constructor(directoryName, parentDirectoryName) {
        this.ItemId = directoryName;
        this.Name = directoryName;
        this.ParentDirectoryId = parentDirectoryName;
    }
}
@Injectable({
    providedIn: 'root',
})
export class DeltaStorageService {
    private readonly renderer: Renderer2;
    notFoundStatus: number = 404;
    commonOptions: any = {
        headers: header,
        withCredentials: true,
        observe: 'response',
    };
    storageUrl: any = ShellDomainProvider['api'].Storage;
    deltaStorageUrl = ShellDomainProvider.api.BusinessService;
    uploadUrl: any = ShellDomainProvider['api'].UploadUrl;

    constructor(
        @Inject(PLATFORM_ID) private readonly platformId: string,
        private readonly http: HttpClient,
        rendererFactory: RendererFactory2,
        private readonly utilityService: UtilityService
    ) {
        console.log(
            'Expected DeltaStorageService runs in replace of ecap StorageDataService'
        );
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    uploadFileObjectGenerator(attachments, fileId, appId, tags, metaData) {
        return {
            ItemId: fileId,
            Name: this.utilityService.getNewGuid() + '_' + attachments.name,
            FileData: attachments,
            Tags: tags ? JSON.stringify(tags) : JSON.stringify(['File']),
            MetaData: metaData
                ? JSON.stringify(metaData)
                : JSON.stringify({
                    FileInfo: { Value: attachments.name },
                }),
            ParentDirectoryId: '',
        };
    }

    getFileInfo(fileId): Observable<any> {
        //If no file id provide then return default result. Don't call server,
        if (!fileId) {
            return of({
                body: null,
            });
        }

        let params = new HttpParams().append('FileId', fileId);
        let options = this.commonOptions;
        options['params'] = params;
        return this.http
            .get(
                this.deltaStorageUrl + 'DeltaService/Storage/GetFile',
                options
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    getFilesInfo(fileIds): Observable<any> {
        //If no file ids provide then return default result. Don't call server,
        if (!fileIds.length) {
            return of({
                body: [],
            });
        }

        return this.http
            .post(
                this.deltaStorageUrl + '/DeltaService/Storage/GetFiles',
                {
                    FileIds: fileIds,
                },
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    createFormData(
        object: Object,
        form?: FormData,
        namespace?: string
    ): FormData {
        const formData = form || new FormData();
        for (let property in object) {
            if (!object.hasOwnProperty(property) || !object[property]) {
                continue;
            }
            const formKey = namespace ? `${namespace}[${property}]` : property;
            if (object[property] instanceof Date) {
                formData.append(formKey, object[property].toISOString());
            } else if (
                typeof object[property] === 'object' &&
                !(object[property] instanceof File)
            ) {
                this.createFormData(object[property], formData, formKey);
            } else {
                formData.append(formKey, object[property]);
            }
        }
        return formData;
    }

    updateFileInfo(fileData): Observable<any> {
        return this.http
            .post(
                this.storageUrl + 'StorageCommand/UpdateFile',
                fileData,
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    uploadFile(attachments, fileId, appId?, tags?, metaData?): Observable<any> {
        return this.http
            .post(
                this.storageUrl + 'StorageCommand/UploadFile',
                this.createFormData(
                    this.uploadFileObjectGenerator(
                        attachments,
                        fileId,
                        appId,
                        tags,
                        metaData
                    )
                )
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    createParentDirectory(parentDirectory): Observable<any> {
        let newDirectory = new Directory(parentDirectory, null);
        return this.http
            .post(
                this.storageUrl + 'StorageCommand/CreateDirectory',
                newDirectory,
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    createAudioDirectory(parentDirectory): Observable<any> {
        let newDirectory = new Directory(
            parentDirectory + 'Audio',
            parentDirectory
        );
        return this.http
            .post(
                this.storageUrl + 'StorageCommand/CreateDirectory',
                newDirectory,
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    createConvertedAudioDirectory(parentDirectory): Observable<any> {
        let newDirectory = new Directory(
            parentDirectory + 'AudioConverted',
            parentDirectory
        );
        return this.http
            .post(
                this.storageUrl + 'StorageCommand/CreateDirectory',
                newDirectory,
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    createImageDirectory(parentDirectory): Observable<any> {
        let newDirectory = new Directory(
            parentDirectory + 'Image',
            parentDirectory
        );
        return this.http
            .post(
                this.storageUrl + 'StorageCommand/CreateDirectory',
                newDirectory,
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    /**
     *  Fallback: Prev code from ecap core package
     *
     *
     * allDirectoryCreate(directoryName, callback) {
     this.createParentDirectory(directoryName).subscribe(() => {
     this.createAudioDirectory(directoryName).subscribe(() => {
     this.createConvertedAudioDirectory(directoryName).subscribe(() => {
     this.createImageDirectory(directoryName).subscribe(() => {
     callback(true);
     });
     });
     });
     });
     }

     createDirectory(directoryName): Observable<any> {
     let params = new HttpParams().append('DirectoryId', directoryName);
     let options = this.commonOptions;
     options['params'] = params;
     return this.http.get(this.storageUrl + 'StorageQuery/GetDirectroy', options).pipe(
     map(
     (response) => {
     if (!response) {
     return this.allDirectoryCreate(directoryName, (result) => {
     return result;
     });
     } else {
     return 'exists';
     }
     },
     (errorResponse) => {
     if (errorResponse.status == this.notFoundStatus) {
     return this.allDirectoryCreate(directoryName, (result) => {
     return result;
     });
     } else {
     return errorResponse;
     }
     }
     )
     );
     }
     */

    allDirectoryCreate(directoryName: string): Observable<boolean> {
        return this.createParentDirectory(directoryName).pipe(
            concatMap(() => this.createAudioDirectory(directoryName)),
            concatMap(() => this.createConvertedAudioDirectory(directoryName)),
            concatMap(() => this.createImageDirectory(directoryName)),
            map(() => true)
        );
    }

    createDirectory(directoryName: string): Observable<any> {
        const params = new HttpParams().append('DirectoryId', directoryName);
        const options = { ...this.commonOptions, params };

        return this.http
            .get(this.storageUrl + 'StorageQuery/GetDirectroy', options)
            .pipe(
                catchError((errorResponse) => {
                    if (errorResponse.status === this.notFoundStatus) {
                        return of(null);
                    }
                    return throwError(() => errorResponse);
                }),
                concatMap((response) => {
                    if (!response) {
                        return this.allDirectoryCreate(directoryName);
                    }
                    return of('exists');
                })
            );
    }

    deleteFile(itemId): Observable<any> {
        return this.http
            .post(
                this.storageUrl + 'StorageCommand/DeleteFile',
                { ItemId: itemId },
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    deleteAllFiles(itemIds): Observable<any> {
        return this.http
            .post(
                this.storageUrl + 'StorageCommand/DeleteAll',
                { ItemIds: itemIds },
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    downloadFile(fileId): Observable<any> {
        let params = new HttpParams().append('FileId', fileId);
        let options = this.commonOptions;
        options['params'] = params;
        return this.http
            .get(this.storageUrl + 'StorageQuery/GetDownloadUri', options)
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    executePipeline(pipelineCommand): Observable<any> {
        return this.http
            .post(
                this.storageUrl +
                'StorageCommand/ExecuteFileConversionPipeline',
                pipelineCommand,
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    generatePSU(
        itemId,
        name,
        parentDirectory,
        tags,
        accesModifier?,
        metaData?
    ) {
        let tempMetaData = {
            Title: {
                Type: 'String',
                Value: name,
            },
            OriginalName: {
                Type: 'String',
                Value: name,
            },
        };
        metaData = metaData || tempMetaData;
        return {
            ItemId: itemId,
            MetaData: JSON.stringify(metaData),
            Name: name,
            ParentDirectoryId: parentDirectory,
            Tags: tags ? JSON.stringify(tags) : JSON.stringify(['File']),
            AccessModifier: accesModifier,
            AllowedFeaturesToReadFile: [],
            AllowedFeaturesToUpdateFile: []
        };
    }

    uploadPresignedFile(url, file): Observable<any> {
        if (ShellDomainProvider['api'].IsAzStorage) {
            return this.http
                .put(url, file, { headers: { 'x-ms-blob-type': 'BlockBlob' } })
                .pipe(
                    map((response: any) => {
                        return response;
                    })
                );
        }

        return this.http.put(url, file).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    uploadPublicPresignedFile(url, file): Observable<any> {
        const header = ShellDomainProvider['api'].IsAzStorage
            ? { headers: { 'x-ms-blob-type': 'BlockBlob' } }
            : { headers: { 'x-amz-acl': 'public-read' } };
        return this.http.put(url, file, header).pipe(
            map((response: any) => {
                return response;
            })
        );
    }

    getPreSignedUrl(
        itemId,
        name,
        parentDirectory,
        tags,
        accessModifier?
    ): Observable<any> {
        return this.http
            .post(
                this.deltaStorageUrl + 'DeltaService/Storage/GetPreSignedUrlForUploadByRolePriority',
                this.generatePSU(
                    itemId,
                    name,
                    parentDirectory,
                    tags,
                    accessModifier
                ),
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    return response;
                })
            );
    }

    downloadFileAsBlob(data) {
        const decodedUri = decodeURI(data.Url);
        let element = this.renderer.createElement('a');
        this.renderer.setAttribute(element, 'href', decodedUri);
        this.renderer.setAttribute(element, 'target', '_blank');
        this.renderer.setAttribute(element, 'download', data['Name']);
        this.renderer.setStyle(element, 'display', 'none');
        this.renderer.appendChild(document.body, element);
        element.click();
        this.renderer.removeChild(document.body, element);
    }

    /**
     * Fallback: Prev code from ecap core package
     *  // var decodedUri = decodeURI(data.Url);
     // if (navigator.msSaveBlob) {
     // 	this.http.get(decodedUri, {
     // 		responseType: "arraybuffer"
     // 	}).pipe(map((data, headers) => {
     // 		var fileBlob = new Blob([data], { type: 'text/plain' });
     // 		navigator.msSaveBlob(fileBlob, data['Name']);
     // 	}));
     // } else {
     // 	var decodedUri = decodeURI(data.Url);
     // 	let element = this.renderer.createElement('a');
     // 	this.renderer.setAttribute(element, "href", decodedUri);
     // 	this.renderer.setAttribute(element, "target", "_blank");
     // 	this.renderer.setAttribute(element, "download", data["Name"]);
     // 	this.renderer.setStyle(element, "display", "none");
     // 	element.click();
     // 	// var a = document.createElement('a');
     // 	// a.setAttribute('href', decodedUri);
     // 	// a.setAttribute('target', '_blank');
     // 	// a.setAttribute('download', data['Name']);
     // 	// a.style.display = 'none';
     // 	// document.body.appendChild(a);
     // 	// a.click();
     // 	// document.body.removeChild(a);
     // }
     */
    downloadFileAsSecureStorageBlob(data) {
        throw new Error(
            'DeltaStorageService -> downloadFileAsSecureStorageBlob implementation not found'
        );
    }

    downloadStream(data) {
        const blob = new Blob([data], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);

        let element = this.renderer.createElement('a');
        this.renderer.setAttribute(element, 'href', url);
        this.renderer.setAttribute(element, 'target', '_blank');
        this.renderer.setAttribute(element, 'download', data['Name']);
        this.renderer.setStyle(element, 'display', 'none');
        element.click();
    }

    downloadSecureStorageFile(url) {
        return this.http.get(url, {
            headers: header,
            withCredentials: true,
            observe: 'response',
        });
    }

    getUrlForExportCSVFile(csvObject): Observable<any> {
        let payload = {
            FilterQuery: csvObject.queryString,
            EntityName: csvObject.entityName,
            ColumnTemplates: csvObject.csvColumnMapingFilter,
            TimezoneOffset: new Date().getTimezoneOffset(),
            FileNameWithExtension: csvObject.fileName + '.csv',
        };
        if (csvObject['fileId']) {
            payload['FileId'] = csvObject['fileId'];
        } else {
            payload['FileId'] = this.utilityService.getNewGuid();
        }
        return this.http
            .post(
                ShellDomainProvider['api'].DataCore +
                'Command/ExportEntityData',
                payload,
                this.commonOptions
            )
            .pipe(
                map((response: any) => {
                    response['fileId'] = payload['FileId'];
                    return response;
                })
            );
    }

    getResources(payload): Observable<any> {
        return this.http
            .post(
                ShellDomainProvider['api'].Storage +
                'StorageQuery/GetResources',
                payload,
                {
                    headers: header,
                    withCredentials: true,
                    observe: 'response',
                }
            )
            .pipe(
                map((response: any) => {
                    if (deepSafeAccess(response, ['body'])) {
                        return response.body;
                    }

                    return null;
                })
            );
    }
    uploadPresignedFileWithProgress(FileInfo, url, file): Observable<any> {
        return new Observable((observer) => {
            let xhr = new XMLHttpRequest();
            xhr.open('PUT', url);
            xhr.setRequestHeader('content-type', file.type);
            if (ShellDomainProvider['api'].IsAzStorage) {
                xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
            }
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    let uploadPercentage = (event.loaded / event.total) * 100;
                    observer.next({
                        uploadPercentage: uploadPercentage,
                        file: FileInfo,
                    });
                }
            });
            xhr.upload.addEventListener('error', (event) => {
                observer.error({ error: event, file: FileInfo });
            });
            xhr.onreadystatechange = function () {
                if (xhr.readyState === 4) {
                    if (xhr['status'] === 200 || xhr['status'] === 201) {
                        observer.next({
                            uploadPercentage: 100,
                            file: FileInfo,
                        });
                        observer.complete();
                    } else {
                        observer.error({
                            error: 'upload error',
                            file: FileInfo,
                        });
                    }
                }
            };
            xhr.send(file);
        });
    }
}
